import React, { FC, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useImageUpload } from '../../hooks/use_image_upload'
import {
  ApiErrorResponse,
  getKyousouApiClient,
  handleError,
} from '../../services/network'
import {
  FormField,
  FieldGroup,
  TextInput,
  TextareaInput,
  DateInput,
  SelectInput,
  RadioInput,
  CheckboxInput,
  ReadonlyText,
} from '../../components/form'
import { useModal } from 'react-hooks-use-modal'
import { useLoading } from '../../providers/loading_provider'
import { useSnackBar } from '../../providers/snack_bar_provider'
import { FormErrorMessage } from '../../constants/message'
import FlatButton from '../../components/buttons/flat_button'
import AuthLayout from '../../components/layouts/auth_layout'
import MainBackground from '../../images/main_bg.png'
import NoImage from '../../images/noimage.png'
import { PrefectureCategoryValues } from '../../enums/PrefectureCategory'
import {
  IndustryCategoryParentValues,
  IndustryCategoryChildValues,
  IndustryCategoryCodeRelations,
} from '../../enums/IndustryCategory'
import {
  BusinessCategoryParentValues,
  BusinessCategoryChildValues,
  BusinessCategoryCodeRelations,
} from '../../enums/BusinessCategory'
import { EnterpriseCategoryValues } from '../../enums/EnterpriseCategory'
import { SDGsCategoryValues } from '../../enums/SDGsCategory'
import RegisterPolicyForm from './register_policy_form'

// 企業登録フォームのプロパティ定義
type EnterpriseRegisterFormProps = {
  token: string
  iconUrl: string
  name: string
  description: string
  delegateLastName: string
  delegateFirstName: string
  delegatePosition: string
  responsiblePersonName: string
  establishDate: string
  prefecture: string
  postCode: string
  address: string
  tel: string
  stockCode: string
  capitalPrice: number | null
  employeeNum: number | null
  industryParentCode: string
  industryChildCode: string
  interestingSDGs: Array<string>
  enterpriseCode: string
  businessCode1: string
  businessCode2: string
  businessCode3: string
  businessChildCode1: string
  businessChildCode2: string
  businessChildCode3: string
  officialUrl: string
  twitterUrl: string
  facebookUrl: string
  lineUrl: string
  tiktokUrl: string
  publicDisplayFlag: boolean
  email: string
  password: string
  passwordConfirmation: string
  agree: boolean
}

// 登録ページのメインコンポーネント
const RegisterPage: FC = () => {
  const navigate = useNavigate()
  const { upload } = useImageUpload()
  const { showLoading, hideLoading } = useLoading()
  const { showSnackBar } = useSnackBar()
  const pathParams = useParams<{ token: string }>()
  const kyousouApiClient = getKyousouApiClient()
  const [ConfirmModal, openConfirmModal, closeConfirmModal] = useModal('root', {
    preventScroll: false,
    focusTrapOptions: {
      clickOutsideDeactivates: true,
    },
  })

  const { register, handleSubmit, watch, getValues, setValue, formState } =
    useForm<EnterpriseRegisterFormProps>({
      defaultValues: {
        token: '',
        iconUrl: NoImage,
        name: '',
        description: '',
        delegateLastName: '',
        delegateFirstName: '',
        delegatePosition: '',
        responsiblePersonName: '',
        establishDate: '',
        prefecture: '',
        postCode: '',
        address: '',
        tel: '',
        stockCode: '',
        capitalPrice: null,
        employeeNum: null,
        industryParentCode: '',
        industryChildCode: '',
        interestingSDGs: [],
        enterpriseCode: '',
        businessCode1: '',
        businessChildCode1: '',
        businessCode2: '',
        businessChildCode2: '',
        businessCode3: '',
        businessChildCode3: '',
        officialUrl: '',
        twitterUrl: '',
        facebookUrl: '',
        lineUrl: '',
        tiktokUrl: '',
        publicDisplayFlag: false,
        email: '',
        password: '',
        passwordConfirmation: '',
        agree: false,
      },
      mode: 'onSubmit',
    })

  // トークンが存在する場合にトークンのチェックを行う
  useEffect(() => {
    if (pathParams.token) {
      checkToken(pathParams.token)
    }
  }, [pathParams.token])

  const { errors } = formState

  // 企業登録処理
  const registerEnterprise = async (data: EnterpriseRegisterFormProps) => {
    // パスワードの確認
    if (data.password !== data.passwordConfirmation) {
      showSnackBar(['パスワードと確認用パスワードが一致しません'], 'error')
      return
    }

    showLoading()

    try {
      await kyousouApiClient.enterprise.registerCreate({
        token: data.token,
        name: data.name,
        description: data.description,
        delegateLastName: data.delegateLastName,
        delegateFirstName: data.delegateFirstName,
        delegatePosition: data.delegatePosition,
        responsiblePersonName: data.responsiblePersonName,
        establishDate: data.establishDate,
        prefecture: data.prefecture,
        postCode: data.postCode,
        address: data.address,
        tel: data.tel,
        stockCode: data.stockCode,
        capitalPrice: data.capitalPrice,
        employeeNum: data.employeeNum,
        industryParentCode: data.industryParentCode,
        industryChildCode: data.industryChildCode,
        enterpriseCode: data.enterpriseCode,
        businessCode: [
          data.businessCode1,
          data.businessCode2,
          data.businessCode3,
        ].filter((code) => code !== ''), // 空文字のコードを除外
        businessChildCode: [
          data.businessChildCode1,
          data.businessChildCode2,
          data.businessChildCode3,
        ].filter((code) => code !== ''), // 空文字のコードを除外
        sdgsCategory: data.interestingSDGs,
        logoUrl: data.iconUrl,
        officialUrl: data.officialUrl,
        twitterUrl: data.twitterUrl,
        facebookUrl: data.facebookUrl,
        lineUrl: data.lineUrl,
        tiktokUrl: data.tiktokUrl,
        publicDisplayFlag: data.publicDisplayFlag,
        email: data.email,
        password: data.password,
      })

      navigate('/register/complete')
    } catch (e) {
      handleError(e as ApiErrorResponse, showSnackBar)
    } finally {
      hideLoading()
    }
  }

  // トークンのチェック
  const checkToken = async (token: string | undefined) => {
    if (!token) return
    try {
      const response =
        await kyousouApiClient.enterprise.registerTokenCheckCreate({
          token: token,
        })
      setValue('token', token)
      setValue('email', response.data.email)
    } catch (error) {
      showSnackBar(['トークンが無効です'], 'error')
      navigate('/login')
    }
  }

  // アイコンをアップロード
  const uploadIcon = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files
    if (!files || files?.length === 0) return

    showLoading()
    const nextFile = files[0]
    const response = await upload(nextFile)
    if (response !== null) {
      setValue('iconUrl', response.url)
    }
    hideLoading()
  }

  const showConfirmModal = async () => {
    const data = getValues()

    // ロゴ画像の確認
    if (!data.iconUrl || data.iconUrl === NoImage) {
      showSnackBar(['ロゴ画像をアップロードしてください'], 'error')
      return
    }

    openConfirmModal()
    // モーダル表示後に一番上にスクロール
    setTimeout(() => {
      const modalElement = document.querySelector(
        '[role="dialog"]'
      ) as HTMLElement
      if (modalElement) {
        const scrollableContent = modalElement.querySelector('.overflow-y-auto')
        if (scrollableContent) {
          scrollableContent.scrollTop = 0
        }
      }
    }, 100)
  }

  const enterpriseForm = (
    <div className="max-w-5xl w-full bg-white rounded-xl py-8">
      <div className="w-11/12 mx-auto">
        <h1 className="text-2xl font-bold text-center mb-8">会員登録</h1>
        <form onSubmit={handleSubmit(showConfirmModal)}>
          {/* ロゴ */}
          <FormField label="ロゴ画像" required>
            <div className="flex justify-start mb-2">
              <div className="rounded-full w-fit">
                <div
                  className="w-24 sm:w-32 h-24 sm:h-32 bg-cover rounded bg-center"
                  style={{
                    backgroundImage: `url(${watch('iconUrl')})`,
                  }}
                ></div>
              </div>
              <div className="flex flex-col justify-center ml-4">
                <label
                  htmlFor="image-upload"
                  className="p-2 border border-black rounded-lg cursor-pointer"
                >
                  画像をアップロード
                </label>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={uploadIcon}
                />
              </div>
            </div>
            <div className="text-sm">※データ形式はjpg、pngのみ対応</div>
          </FormField>

          <FormField label="メールアドレス" required>
            <ReadonlyText value={watch('email')} />
          </FormField>

          <FormField
            label="企業・団体名"
            required
            description="企業・団体名をご入力ください。"
            error={errors.name?.message}
          >
            <TextInput
              register={register('name', {
                required: FormErrorMessage.required,
                maxLength: {
                  value: 50,
                  message: FormErrorMessage.maxLength(50),
                },
              })}
            />
          </FormField>

          <FormField
            label="企業・団体説明"
            description="企業・団体説明をご入力ください。"
          >
            <TextareaInput
              register={register('description', {
                maxLength: {
                  value: 200,
                  message: FormErrorMessage.maxLength(200),
                },
              })}
            />
          </FormField>
          {errors.description && (
            <p className="error-message">{errors.description.message}</p>
          )}

          <FieldGroup
            required
            description="代表者氏名をご入力ください。"
            fields={[
              {
                label: '代表者名前（姓）',
                error: errors.delegateLastName?.message,
                children: (
                  <TextInput
                    register={register('delegateLastName', {
                      required: FormErrorMessage.required,
                      maxLength: {
                        value: 50,
                        message: FormErrorMessage.maxLength(50),
                      },
                    })}
                  />
                ),
              },
              {
                label: '代表者名前（名）',
                error: errors.delegateFirstName?.message,
                children: (
                  <TextInput
                    register={register('delegateFirstName', {
                      required: FormErrorMessage.required,
                      maxLength: {
                        value: 50,
                        message: FormErrorMessage.maxLength(50),
                      },
                    })}
                  />
                ),
              },
            ]}
          />

          <FormField
            label="代表者役職"
            required
            description="役職をご入力ください。"
            error={errors.delegatePosition?.message}
          >
            <TextInput
              register={register('delegatePosition', {
                required: FormErrorMessage.required,
                maxLength: {
                  value: 50,
                  message: FormErrorMessage.maxLength(50),
                },
              })}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="担当者名"
            required
            description="担当者名をご入力ください。"
            error={errors.responsiblePersonName?.message}
          >
            <TextInput
              register={register('responsiblePersonName', {
                required: FormErrorMessage.required,
                maxLength: {
                  value: 50,
                  message: FormErrorMessage.maxLength(50),
                },
              })}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="会社設立日"
            required
            description="会社設立日をご入力ください。"
            error={errors.establishDate?.message}
          >
            <DateInput
              register={register('establishDate', {
                required: FormErrorMessage.required,
              })}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="本社所在地（郵便番号）"
            required
            description="郵便番号をご入力ください。"
            error={errors.postCode?.message}
          >
            <TextInput
              register={register('postCode', {
                required: FormErrorMessage.required,
                minLength: {
                  value: 7,
                  message: FormErrorMessage.minLength(7),
                },
                maxLength: {
                  value: 7,
                  message: FormErrorMessage.maxLength(7),
                },
              })}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="本社所在地（都道府県）"
            required
            description="都道府県を選択してください。"
            error={errors.prefecture?.message}
          >
            <SelectInput
              register={register('prefecture', {
                required: FormErrorMessage.required,
              })}
              options={PrefectureCategoryValues.map((value) => ({
                value: value,
                label: value,
              }))}
              placeholder="選択してください"
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="本社所在地（市区町村以降）"
            required
            description="住所をご入力ください。"
            error={errors.address?.message}
          >
            <TextInput
              register={register('address', {
                required: FormErrorMessage.required,
              })}
              width="100%"
            />
          </FormField>

          <FormField
            label="代表電話番号"
            required
            description="代表電話番号をご入力ください。"
            error={errors.tel?.message}
          >
            <TextInput
              register={register('tel', {
                required: FormErrorMessage.required,
                minLength: {
                  value: 11,
                  message: FormErrorMessage.minLength(11),
                },
                maxLength: {
                  value: 15,
                  message: FormErrorMessage.maxLength(15),
                },
              })}
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="証券コード"
            description="証券コードをご入力ください。"
          >
            <TextInput
              register={register('stockCode')}
              className="w-full md:w-96"
            />
          </FormField>
          {errors.stockCode && (
            <p className="error-message">{errors.stockCode.message}</p>
          )}

          <FormField label="資本金" description="資本金をご入力ください。">
            <TextInput
              register={register('capitalPrice')}
              type="number"
              unit="円"
              className="w-full md:w-96"
            />
          </FormField>
          {errors.capitalPrice && (
            <p className="error-message">{errors.capitalPrice.message}</p>
          )}

          <FormField label="社員数" description="社員数をご入力ください。">
            <TextInput
              register={register('employeeNum')}
              type="number"
              unit="人"
              className="w-full md:w-96"
            />
          </FormField>
          {errors.employeeNum && (
            <p className="error-message">{errors.employeeNum.message}</p>
          )}

          <FormField
            label="公式サイトURL"
            description="公式サイトURLをご入力ください。"
          >
            <TextInput register={register('officialUrl')} width="100%" />
          </FormField>
          {errors.officialUrl && (
            <p className="error-message">{errors.officialUrl.message}</p>
          )}

          <FormField
            label="X（旧Twitter）URL"
            description="X（旧Twitter）URLをご入力ください。"
          >
            <TextInput register={register('twitterUrl')} width="100%" />
          </FormField>
          {errors.twitterUrl && (
            <p className="error-message">{errors.twitterUrl.message}</p>
          )}

          <FormField
            label="Facebook URL"
            description="Facebook URLをご入力ください。"
          >
            <TextInput register={register('facebookUrl')} width="100%" />
          </FormField>
          {errors.facebookUrl && (
            <p className="error-message">{errors.facebookUrl.message}</p>
          )}

          <FormField
            label="公式LINE URL"
            description="公式LINE URLをご入力ください。"
          >
            <TextInput register={register('lineUrl')} width="100%" />
          </FormField>
          {errors.lineUrl && (
            <p className="error-message">{errors.lineUrl.message}</p>
          )}

          <FormField
            label="TikTok URL"
            description="TikTok URLをご入力ください。"
          >
            <TextInput register={register('tiktokUrl')} width="100%" />
          </FormField>
          {errors.tiktokUrl && (
            <p className="error-message">{errors.tiktokUrl.message}</p>
          )}

          <FieldGroup
            required
            fields={[
              {
                label: '業種カテゴリー（大）',
                error: errors.industryParentCode?.message,
                children: (
                  <SelectInput
                    register={register('industryParentCode', {
                      required: FormErrorMessage.required,
                    })}
                    options={IndustryCategoryParentValues.map((item) => ({
                      value: item.code,
                      label: item.value,
                    }))}
                    placeholder="選択してください"
                  />
                ),
              },
              {
                label: '業種カテゴリー（小）',
                error: errors.industryChildCode?.message,
                children: (
                  <SelectInput
                    register={register('industryChildCode', {
                      required: FormErrorMessage.required,
                    })}
                    options={IndustryCategoryChildValues.filter((item) =>
                      IndustryCategoryCodeRelations[
                        watch('industryParentCode')
                      ]?.includes(item.code)
                    ).map((item) => ({
                      value: item.code,
                      label: item.value,
                    }))}
                    placeholder="選択してください"
                  />
                ),
              },
            ]}
          />

          <FormField
            label="企業・団体カテゴリー"
            required
            error={errors.enterpriseCode?.message}
          >
            <RadioInput
              name="enterprise"
              register={register('enterpriseCode', {
                required: FormErrorMessage.required,
              })}
              options={EnterpriseCategoryValues.map((item) => ({
                value: item.code,
                label: item.value,
              }))}
            />
          </FormField>

          <FieldGroup
            required
            fields={[
              {
                label: 'ビジネスカテゴリー（大）1',
                error: errors.businessCode1?.message,
                children: (
                  <SelectInput
                    register={register('businessCode1', {
                      required: FormErrorMessage.required,
                    })}
                    options={BusinessCategoryParentValues.map((item) => ({
                      value: item.code,
                      label: item.value,
                    }))}
                    placeholder="選択してください"
                  />
                ),
              },
              {
                label: 'ビジネスカテゴリー（小）1',
                error: errors.businessChildCode1?.message,
                children: (
                  <SelectInput
                    register={register('businessChildCode1', {
                      required:
                        watch('businessCode1') !== ''
                          ? FormErrorMessage.required
                          : undefined,
                    })}
                    options={BusinessCategoryChildValues.filter((item) =>
                      BusinessCategoryCodeRelations[
                        watch('businessCode1')
                      ]?.includes(item.code)
                    ).map((item) => ({
                      value: item.code,
                      label: item.value,
                    }))}
                    placeholder="選択してください"
                  />
                ),
              },
            ]}
          />

          <FieldGroup
            fields={[
              {
                label: 'ビジネスカテゴリー（大）2',
                children: (
                  <SelectInput
                    register={register('businessCode2')}
                    options={BusinessCategoryParentValues.map((item) => ({
                      value: item.code,
                      label: item.value,
                    }))}
                    placeholder="選択してください"
                  />
                ),
              },
              {
                label: 'ビジネスカテゴリー（小）2',
                children: (
                  <SelectInput
                    register={register('businessChildCode2', {
                      required:
                        watch('businessCode2') !== ''
                          ? FormErrorMessage.required
                          : undefined,
                    })}
                    options={BusinessCategoryChildValues.filter((item) =>
                      BusinessCategoryCodeRelations[
                        watch('businessCode2')
                      ]?.includes(item.code)
                    ).map((item) => ({
                      value: item.code,
                      label: item.value,
                    }))}
                    placeholder="選択してください"
                  />
                ),
              },
            ]}
          />
          {errors.businessCode2 && (
            <p className="error-message">{errors.businessCode2.message}</p>
          )}
          {errors.businessChildCode2 && (
            <p className="error-message">{errors.businessChildCode2.message}</p>
          )}

          <FieldGroup
            fields={[
              {
                label: 'ビジネスカテゴリー（大）3',
                children: (
                  <SelectInput
                    register={register('businessCode3')}
                    options={BusinessCategoryParentValues.map((item) => ({
                      value: item.code,
                      label: item.value,
                    }))}
                    placeholder="選択してください"
                  />
                ),
              },
              {
                label: 'ビジネスカテゴリー（小）3',
                children: (
                  <SelectInput
                    register={register('businessChildCode3', {
                      required:
                        watch('businessCode3') !== ''
                          ? FormErrorMessage.required
                          : undefined,
                    })}
                    options={BusinessCategoryChildValues.filter((item) =>
                      BusinessCategoryCodeRelations[
                        watch('businessCode3')
                      ]?.includes(item.code)
                    ).map((item) => ({
                      value: item.code,
                      label: item.value,
                    }))}
                    placeholder="選択してください"
                  />
                ),
              },
            ]}
          />
          {errors.businessCode3 && (
            <p className="error-message">{errors.businessCode3.message}</p>
          )}
          {errors.businessChildCode3 && (
            <p className="error-message">{errors.businessChildCode3.message}</p>
          )}

          <FormField
            label="SDGsカテゴリー"
            required
            error={errors.interestingSDGs?.message}
          >
            <CheckboxInput
              name="sdgs"
              register={register('interestingSDGs', {
                required: FormErrorMessage.required,
              })}
              options={SDGsCategoryValues.map((item) => ({
                value: item.code,
                label: item.code + '. ' + item.value,
              }))}
              layout="vertical"
            />
          </FormField>

          <FormField
            label="パスワード"
            required
            description="パスワードをご入力ください。"
            error={errors.password?.message}
          >
            <TextInput
              register={register('password', {
                required: FormErrorMessage.required,
                minLength: {
                  value: 8,
                  message: FormErrorMessage.minLength(8),
                },
              })}
              type="password"
              className="w-full md:w-96"
            />
          </FormField>

          <FormField
            label="確認用パスワード"
            required
            description="確認用パスワードをご入力ください。"
            error={errors.passwordConfirmation?.message}
          >
            <TextInput
              register={register('passwordConfirmation', {
                required: FormErrorMessage.required,
                minLength: {
                  value: 8,
                  message: FormErrorMessage.minLength(8),
                },
              })}
              type="password"
              className="w-full md:w-96"
            />
          </FormField>

          <RegisterPolicyForm />

          <div className="mb-12">
            <label className="flex items-start justify-center">
              <input
                type="checkbox"
                className="mt-[5px] mr-[4px]"
                {...register('agree', {
                  required: '必ず同意してください',
                })}
              />
              <span className="pl-1">
                上記の利用規約、プライバシーポリシーについて同意する
              </span>
            </label>
            {errors.agree && (
              <p className="error-message text-center">
                {errors.agree.message}
              </p>
            )}
          </div>

          <div className="flex justify-center mt-8">
            <FlatButton type="submit" className="w-full">
              確認する
            </FlatButton>
          </div>
        </form>
      </div>
    </div>
  )

  const confirmContent = (
    <div className="bg-white max-h-[85vh] rounded-md max-w-[90vw] overflow-y-auto overscroll-none  py-8 px-8 w-[640px]">
      <p className="mb-4">以下の内容で会員登録します。</p>
      <div className="mb-8">
        <div className="mb-4">
          <div className="text-gray-400">プロフィール画像</div>
          <div>
            <div
              className="w-16 h-16 bg-cover rounded bg-center"
              style={{
                backgroundImage: `url(${getValues('iconUrl')})`,
              }}
            ></div>
          </div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">メールアドレス</div>
          <div>{getValues('email')}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">企業・団体名</div>
          <div>{getValues('name')}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">企業・団体説明</div>
          <div>{getValues('description') || '未入力'}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">代表者名前</div>
          <div>
            {getValues('delegateLastName')} {getValues('delegateFirstName')}
          </div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">代表者役職</div>
          <div>{getValues('delegatePosition')}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">担当者名</div>
          <div>{getValues('responsiblePersonName')}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">会社設立日</div>
          <div>{getValues('establishDate').replaceAll('-', '/')}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">本社所在地（郵便番号）</div>
          <div>{getValues('postCode')}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">本社所在地（都道府県）</div>
          <div>{getValues('prefecture')}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">本社所在地（市区町村以降）</div>
          <div>{getValues('address')}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">代表電話番号</div>
          <div>{getValues('tel')}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">証券コード</div>
          <div>{getValues('stockCode') || '未入力'}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">資本金</div>
          <div>
            {getValues('capitalPrice') !== null
              ? getValues('capitalPrice')?.toLocaleString() + '円'
              : '未入力'}
          </div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">社員数</div>
          <div>
            {getValues('employeeNum') !== null
              ? getValues('employeeNum')?.toLocaleString() + '人'
              : '未入力'}
          </div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">公式サイトURL</div>
          <div>{getValues('officialUrl') || '未入力'}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">X（旧Twitter）URL</div>
          <div>{getValues('twitterUrl') || '未入力'}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">Facebook URL</div>
          <div>{getValues('facebookUrl') || '未入力'}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">公式LINE URL</div>
          <div>{getValues('lineUrl') || '未入力'}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">TikTok URL</div>
          <div>{getValues('tiktokUrl') || '未入力'}</div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">業種カテゴリー</div>
          <div>
            {getValues('industryParentCode') && (
              <div className="w-full flex flex-wrap mb-3">
                <p className="pr-8">
                  {
                    IndustryCategoryParentValues.find(
                      (item) => item.code === getValues('industryParentCode')
                    )?.value
                  }
                </p>
                <p>
                  {
                    IndustryCategoryChildValues.find(
                      (item) => item.code === getValues('industryChildCode')
                    )?.value
                  }
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">企業・団体カテゴリー</div>
          <div>
            {
              EnterpriseCategoryValues.find(
                (item) => item.code === getValues('enterpriseCode')
              )?.value
            }
          </div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">ビジネスカテゴリー</div>
          <div>
            {getValues('businessCode1') && (
              <div className="w-full flex flex-wrap mb-3">
                <p className="pr-8">
                  {
                    BusinessCategoryParentValues.find(
                      (item) => item.code === getValues('businessCode1')
                    )?.value
                  }
                </p>
                <p className="">
                  {
                    BusinessCategoryChildValues.find(
                      (item) => item.code === getValues('businessChildCode1')
                    )?.value
                  }
                </p>
              </div>
            )}
            {getValues('businessCode2') && (
              <div className="w-full flex flex-wrap mb-3">
                <p className="pr-8">
                  {
                    BusinessCategoryParentValues.find(
                      (item) => item.code === getValues('businessCode2')
                    )?.value
                  }
                </p>
                <p>
                  {
                    BusinessCategoryChildValues.find(
                      (item) => item.code === getValues('businessChildCode2')
                    )?.value
                  }
                </p>
              </div>
            )}
            {getValues('businessCode3') && (
              <div className="w-full flex flex-wrap mb-3">
                <p className="pr-8">
                  {
                    BusinessCategoryParentValues.find(
                      (item) => item.code === getValues('businessCode3')
                    )?.value
                  }
                </p>
                <p>
                  {
                    BusinessCategoryChildValues.find(
                      (item) => item.code === getValues('businessChildCode3')
                    )?.value
                  }
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">SDGsカテゴリー</div>
          <div>
            {getValues('interestingSDGs').map((item) => {
              const sdgsCategory = SDGsCategoryValues.find(
                (sdgs) => sdgs.code === item
              )

              return sdgsCategory ? <p>{sdgsCategory.value}</p> : <></>
            })}
          </div>
        </div>
        <div className="mb-4">
          <div className="text-gray-400">パスワード</div>
          <div>{getValues('password').replace(/./g, '*')}</div>
        </div>
      </div>
      <div className="sm:flex justify-center mt-8">
        <FlatButton
          className="mx-auto sm:mx-3 mb-4"
          onClick={handleSubmit(registerEnterprise)}
        >
          会員登録する
        </FlatButton>
        <FlatButton
          className="mx-auto sm:mx-3 mb-4 bg-gray-200"
          onClick={closeConfirmModal}
        >
          閉じる
        </FlatButton>
      </div>
    </div>
  )

  return (
    <AuthLayout>
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat z-[-1]"
        style={{
          backgroundImage: `url(${MainBackground})`,
        }}
      />
      <div className="w-full overflow-auto py-24 px-4">
        <div className="flex flex-col items-center">{enterpriseForm}</div>
        <ConfirmModal>{confirmContent}</ConfirmModal>
      </div>
    </AuthLayout>
  )
}

export default RegisterPage
